/**
 * Domain colour mapping for the 7 AI risk domains used in Incident Tracker
 * charts. Hex values come from the AIRI Navigator design system palette
 * (see "Domain Taxonomy" in the IMS design system reference) so charts
 * embedded in the Navigator and standalone pages share one visual identity.
 */

export const DOMAIN_COLORS: Record<string, string> = {
  '1 Discrimination & Toxicity': '#C24060',
  '2 Privacy & Security': '#2e5a88',
  '3 Misinformation': '#d4762c',
  '4 Malicious actors': '#6b3a7d',
  '5 Human-Computer Interaction': '#2a7d4f',
  '6 Socioeconomic & Environmental': '#c4a32e',
  '7 AI system safety, failures, & limitations': '#4a8c8c',
};

/** Ordered list of domain names matching canonical sort order */
export const DOMAIN_ORDER = [
  '1 Discrimination & Toxicity',
  '2 Privacy & Security',
  '3 Misinformation',
  '4 Malicious actors',
  '5 Human-Computer Interaction',
  '6 Socioeconomic & Environmental',
  '7 AI system safety, failures, & limitations',
];

/**
 * Get colour for a domain name. Falls back to grey for unknown domains.
 */
export function getDomainColor(domain: string): string {
  return DOMAIN_COLORS[domain] || '#999999';
}

/**
 * Subdomain colours: derived from parent domain colour with slight variation.
 * We use the parent domain colour for each subdomain since there are many subdomains
 * and the parent domain colour provides visual grouping.
 */
export function getSubdomainColor(subdomain: string): string {
  // Extract domain number from subdomain string like "1.1 Unfair discrimination..."
  const domainNum = subdomain.match(/^(\d+)\./)?.[1];
  if (!domainNum) return '#999999';

  const domainName = DOMAIN_ORDER.find((d) => d.startsWith(`${domainNum} `));
  if (!domainName) return '#999999';

  return DOMAIN_COLORS[domainName] || '#999999';
}

/**
 * Subdomain palette indexed by domain number. Each domain contains the
 * canonical AIRI Navigator subdomain shades in subdomain order (1.1, 1.2, ...).
 * Hexes mirror the "Subdomain Colors" block in the design system reference.
 */
const SUBDOMAIN_PALETTE: Record<string, string[]> = {
  '1': ['#872b42', '#b83b59', '#ce647e'],
  '2': ['#2d5885', '#6798cb'],
  '3': ['#93521f', '#db9157'],
  '4': ['#68397a', '#8e4da5', '#aa74be'],
  '5': ['#2d8654', '#66cc94'],
  '6': ['#917822', '#a58927', '#ba9b2c', '#cfac30', '#d3b445', '#d8bc5a'],
  '7': ['#3e7575', '#478585', '#4f9696', '#58a7a7', '#69b0b0', '#7ab8b8'],
};

export function getSubdomainPaletteColor(subdomain: string, indexInDomain: number): string {
  const domainNum = subdomain.match(/^(\d+)\./)?.[1];
  if (!domainNum) return '#999999';
  const palette = SUBDOMAIN_PALETTE[domainNum];
  if (!palette) return '#999999';
  return palette[indexInDomain % palette.length];
}
